import { Invoice, InvoiceConfidence } from 'openapi';

import { CONFIDENCE_STATUS_COLORS } from 'utils/constants/confidenceStatusColors';
import {
  NON_PARTICIPATING_SECTION_IDS,
  OverallConfidenceSection,
  PARTICIPANT_SECTION_IDS
} from 'utils/constants/invoiceVerificationSections';
import { ConfidenceLevel, SectionStatus } from 'utils/enums/Confidence';
import { TranslateFunction } from 'utils/interfaces/Translations';

export type { OverallConfidenceSection };

export type InvoiceConfidenceFieldKey = keyof InvoiceConfidence;

export const HIGH_CONFIDENCE_THRESHOLD = 0.85;
export const LOW_CONFIDENCE_THRESHOLD = 0.5;

export const getInvoiceHasConfidenceData = (invoice: Invoice): boolean =>
  Boolean(invoice.confidence && Object.keys(invoice.confidence).length > 0);

export const getConfidenceLevel = (score?: number | null): ConfidenceLevel => {
  if (score === undefined || score === null) {
    return ConfidenceLevel.UNKNOWN;
  }
  if (score >= HIGH_CONFIDENCE_THRESHOLD) {
    return ConfidenceLevel.HIGH;
  }
  if (score >= LOW_CONFIDENCE_THRESHOLD) {
    return ConfidenceLevel.MEDIUM;
  }
  return ConfidenceLevel.LOW;
};

const FIELD_TO_CONFIDENCE_KEY: Record<string, keyof InvoiceConfidence> = {
  companyId: 'receiver',
  documentType: 'documentType',
  counterpartyId: 'counterparty',
  counterpartyName: 'counterparty',
  counterpartyRegistrationNumber: 'counterparty',
  counterpartyVatNumber: 'counterparty',
  invoiceNumber: 'invoiceNumber',
  invoiceDate: 'invoiceDate',
  invoiceAmount: 'invoiceAmount',
  vatBase: 'vatBase',
  vatAmount: 'vatAmount',
  currencyId: 'currency'
};

const BLOCKING_CONFIDENCE_FIELDS: Array<keyof InvoiceConfidence> = [
  'receiver',
  'documentType',
  'counterparty',
  'invoiceNumber',
  'invoiceDate',
  'invoiceAmount',
  'vatBase',
  'vatAmount',
  'currency'
];

const CONFIDENCE_KEY_TO_FIELDS: Record<keyof InvoiceConfidence, string[]> = {
  receiver: ['companyId'],
  documentType: ['documentType'],
  counterparty: [
    'counterpartyId',
    'counterpartyName',
    'counterpartyRegistrationNumber',
    'counterpartyVatNumber'
  ],
  invoiceNumber: ['invoiceNumber'],
  invoiceDate: ['invoiceDate'],
  invoiceAmount: ['invoiceAmount'],
  vatBase: ['vatBase'],
  vatAmount: ['vatAmount'],
  currency: ['currencyId']
};

export const getFieldConfidenceKey = (
  fieldName: string
): keyof InvoiceConfidence | undefined => FIELD_TO_CONFIDENCE_KEY[fieldName];

export const getFieldConfidence = (
  invoice: Invoice,
  fieldName: string
): number | null => {
  const confidenceKey = getFieldConfidenceKey(fieldName);
  if (!confidenceKey) {
    return null;
  }
  return invoice.confidence?.[confidenceKey] ?? null;
};

export const getFieldConfidenceLevel = (
  invoice: Invoice,
  fieldName: string,
  isDirty = false
): ConfidenceLevel => {
  if (isDirty) {
    return ConfidenceLevel.HIGH;
  }
  const score = getFieldConfidence(invoice, fieldName);
  return getConfidenceLevel(score);
};

export const getSectionConfidence = (
  invoice: Invoice,
  fieldNames: string[],
  dirtyFieldNames: string[] = []
): ConfidenceLevel => {
  if (!fieldNames.length) {
    return ConfidenceLevel.HIGH;
  }

  let hasMedium = false;
  let hasKnownLevel = false;

  for (const fieldName of fieldNames) {
    const level = getFieldConfidenceLevel(
      invoice,
      fieldName,
      dirtyFieldNames.includes(fieldName)
    );

    if (level === ConfidenceLevel.LOW) {
      return ConfidenceLevel.LOW;
    }

    if (level === ConfidenceLevel.MEDIUM) {
      hasMedium = true;
      hasKnownLevel = true;
    } else if (level === ConfidenceLevel.HIGH) {
      hasKnownLevel = true;
    }
  }

  if (!hasKnownLevel) {
    return ConfidenceLevel.UNKNOWN;
  }

  return hasMedium ? ConfidenceLevel.MEDIUM : ConfidenceLevel.HIGH;
};

export const getSectionStatus = (
  confidenceLevel: ConfidenceLevel,
  hasError: boolean
): SectionStatus => {
  if (hasError) return SectionStatus.ERROR;
  if (confidenceLevel === ConfidenceLevel.LOW) {
    return SectionStatus.LOW_CONFIDENCE;
  }
  if (confidenceLevel === ConfidenceLevel.MEDIUM) {
    return SectionStatus.NEEDS_REVIEW;
  }
  return SectionStatus.NONE;
};

export const getSectionShouldAutoExpand = (
  confidenceLevel: ConfidenceLevel,
  hasError: boolean
): boolean =>
  getSectionStatus(confidenceLevel, hasError) !== SectionStatus.NONE;

export const getInvoiceHasLowConfidence = (
  invoice: Invoice,
  dirtyFieldNames: string[] = []
): boolean => {
  const confidence = invoice.confidence;
  if (!confidence) {
    return false;
  }

  return BLOCKING_CONFIDENCE_FIELDS.some((field) => {
    const fieldNames = CONFIDENCE_KEY_TO_FIELDS[field];
    if (fieldNames?.some((fieldName) => dirtyFieldNames.includes(fieldName))) {
      return false;
    }
    const score = confidence[field];
    return (
      score !== undefined && score !== null && score < LOW_CONFIDENCE_THRESHOLD
    );
  });
};

export const getConfidenceSnapshot = (
  invoice: Invoice,
  dirtyFieldNames: string[] = []
): InvoiceConfidence | undefined => {
  const confidence = invoice.confidence;
  if (!confidence) {
    return undefined;
  }

  return (Object.keys(confidence) as Array<keyof InvoiceConfidence>).reduce(
    (snapshot, key) => {
      const fieldNames = CONFIDENCE_KEY_TO_FIELDS[key] || [];
      snapshot[key] = fieldNames.some((fieldName) =>
        dirtyFieldNames.includes(fieldName)
      )
        ? 1
        : (confidence[key] ?? null);
      return snapshot;
    },
    {} as InvoiceConfidence
  );
};

export const getConfidenceColor = (level: ConfidenceLevel) => {
  switch (level) {
    case ConfidenceLevel.HIGH:
      return CONFIDENCE_STATUS_COLORS.confident.main;
    case ConfidenceLevel.MEDIUM:
      return CONFIDENCE_STATUS_COLORS.needsReview.main;
    case ConfidenceLevel.LOW:
      return CONFIDENCE_STATUS_COLORS.lowConfidence.main;
    default:
      return undefined;
  }
};

const CONFIDENCE_HINT_KEY = 'messages.confidenceHint';

export const isFieldConfidenceIssue = (level: ConfidenceLevel): boolean =>
  level === ConfidenceLevel.LOW || level === ConfidenceLevel.MEDIUM;

export const getFieldConfidenceState = (
  invoice: Invoice,
  fieldName: string,
  isDirty = false
): {
  isLow: boolean;
  isMedium: boolean;
  hasIssue: boolean;
} => {
  const level = getFieldConfidenceLevel(invoice, fieldName, isDirty);

  return {
    isLow: level === ConfidenceLevel.LOW,
    isMedium: level === ConfidenceLevel.MEDIUM,
    hasIssue: isFieldConfidenceIssue(level)
  };
};

export const getFieldConfidenceUi = (
  invoice: Invoice,
  fieldName: string,
  translate: TranslateFunction,
  isDirty = false
): {
  isLow: boolean;
  isMedium: boolean;
  hasIssue: boolean;
  confidenceMessage: { text: string } | undefined;
} => {
  const level = getFieldConfidenceLevel(invoice, fieldName, isDirty);

  return {
    isLow: level === ConfidenceLevel.LOW,
    isMedium: level === ConfidenceLevel.MEDIUM,
    hasIssue: isFieldConfidenceIssue(level),
    confidenceMessage: isFieldConfidenceIssue(level)
      ? { text: translate(CONFIDENCE_HINT_KEY) }
      : undefined
  };
};

export type OverallConfidenceState = 'confident' | 'needsReview' | 'blocking';

export type OverallConfidenceReason =
  'lowConfidence' | 'missingData' | 'validationError' | null;

export interface OverallConfidence {
  state: OverallConfidenceState;
  reason: OverallConfidenceReason;
  score: number | null;
  sectionsNeedingReview: number;
}

const getSectionScore = (
  invoice: Invoice,
  fieldNames: string[],
  dirtyFieldNames: string[]
): number | null => {
  let score: number | null = null;

  for (const fieldName of fieldNames) {
    const value = dirtyFieldNames.includes(fieldName)
      ? 1
      : getFieldConfidence(invoice, fieldName);
    if (value === null) {
      continue;
    }
    if (score === null || value < score) {
      score = value;
    }
  }

  return score;
};

export const getOverallConfidence = (
  invoice: Invoice,
  visibleSections: OverallConfidenceSection[] = [],
  dirtyFieldNames: string[] = [],
  businessValidationErrors: string[] = [],
  missingRequiredFieldNames: string[] = []
): OverallConfidence => {
  const isReimbursement = Boolean(invoice.isReimbursement);

  const activeSections = visibleSections.filter((section) => {
    if (NON_PARTICIPATING_SECTION_IDS.includes(section.id)) {
      return false;
    }
    if (PARTICIPANT_SECTION_IDS.includes(section.id)) {
      return isReimbursement
        ? section.id === 'reimbursement'
        : section.id === 'counterparty';
    }
    return true;
  });

  const hasMissingRequiredData = missingRequiredFieldNames.length > 0;

  // a stale validation error on a field whose section is not active
  // (e.g. reimbursementCounterpartyId after reimbursement is switched off)
  // must not block the overall confidence
  const activeSectionFieldNames = new Set(
    activeSections.flatMap((section) => section.fieldNames)
  );
  const relevantValidationErrors = businessValidationErrors.filter(
    (fieldName) => activeSectionFieldNames.has(fieldName)
  );

  if (hasMissingRequiredData) {
    return {
      state: 'blocking',
      reason: 'missingData',
      score: null,
      sectionsNeedingReview: 0
    };
  }

  if (relevantValidationErrors.length > 0) {
    return {
      state: 'blocking',
      reason: 'validationError',
      score: null,
      sectionsNeedingReview: 0
    };
  }

  const evaluatedSections = activeSections.map((section) => {
    const score = getSectionScore(invoice, section.fieldNames, dirtyFieldNames);
    return {
      score,
      level:
        score === null ? ConfidenceLevel.UNKNOWN : getConfidenceLevel(score)
    };
  });

  const coveredSections = evaluatedSections.filter(
    (section) => section.score !== null
  );

  const overallScore = coveredSections.length
    ? Math.min(...coveredSections.map((section) => section.score as number))
    : null;
  const sectionsNeedingReview =
    coveredSections.filter((section) => section.level !== ConfidenceLevel.HIGH)
      .length +
    (evaluatedSections.length - coveredSections.length);

  if (
    coveredSections.some((section) => section.level === ConfidenceLevel.LOW)
  ) {
    return {
      state: 'blocking',
      reason: 'lowConfidence',
      score: overallScore,
      sectionsNeedingReview: evaluatedSections.filter(
        (section) =>
          section.score !== null && section.score < HIGH_CONFIDENCE_THRESHOLD
      ).length
    };
  }

  if (
    coveredSections.length < evaluatedSections.length ||
    (overallScore !== null && overallScore < HIGH_CONFIDENCE_THRESHOLD)
  ) {
    return {
      state: 'needsReview',
      reason: null,
      score: overallScore,
      sectionsNeedingReview
    };
  }

  return {
    state: 'confident',
    reason: null,
    score: overallScore,
    sectionsNeedingReview: 0
  };
};
